import {
	isClaimingFunctionEnabled,
	isEncouragingRereservationEnabled,
	isManuallyClaimingEnabled,
} from "./logic"

import type {
	CancelPolicyTemplateType,
	CancelType,
	ManuallyClaimingUnitKind,
} from "@/souhl_admin/gql/__generated__/graphql"
import type {
	CompanySmsSendingRuleV2EditCancelTypeSetting,
	CompanySmsSendingRuleV2EditType,
} from "@/souhl_admin/utils/form/companySmsSendingRuleV2Edit"

import { cancelFeeTypeForNonZeroYenReservationKey } from "../companySmsSendingRuleV2/CompanySmsSendingRuleV2.const"

// yup の型 => ProcessedCancelTypeSetting => create or update の input のように変換していく。
// - 下記 undefined は create のみで使用し、nullは単純に値を持たない可能性があるもの。
export type ProcessedEncouragingRereservationUnit = {
	minVisitCount: number
	encouragingRereservationPolicyName: string
}
export type ProcessedManuallyClaimingUnit = {
	minVisitCount: number
	manuallyClaimingUnitKind: ManuallyClaimingUnitKind | null
	conversionDays: number | null
}
export type ProcessedCancelTypeSetting = {
	id?: string
	cancelType?: CancelType
	cancelFeePercentForNonZeroYenReservation: number | null
	cancelFeeAmountForNonZeroYenReservation: number | null
	cancelFeeAmountForZeroYenReservation: number
	claimingConversionPolicyName: string | null
	encouragingRereservationPolicyName: string | null
	encouragingRereservationUnits: ProcessedEncouragingRereservationUnit[]
	manuallyClaimingUnits: ProcessedManuallyClaimingUnit[]
	minVisitCountForClaimingConversion: number
	minVisitCountForEncouragingRereservation: number
	minVisitCountForManuallyClaimingCancelFee: number
	minVisitCountForNothing: number
}
export type ProcessedInput = {
	id: string
	name: string
	cancelPolicyTemplateType?: CancelPolicyTemplateType
	cancelTypeSettingInputs: ProcessedCancelTypeSetting[]
}

/**
 * unit を入力用の形 (minVisitCount / encouragingRereservationPolicyName のみ) に落とす。
 * query 結果 (__typename 付き) を form の初期値・送信 payload に詰めるときに使う
 */
export function toEncouragingRereservationUnitInputs(
	units: {
		minVisitCount: number
		encouragingRereservationPolicyName: string
	}[]
): ProcessedEncouragingRereservationUnit[] {
	return units.map((unit) => ({
		minVisitCount: unit.minVisitCount,
		encouragingRereservationPolicyName: unit.encouragingRereservationPolicyName,
	}))
}

/**
 * 手動請求 unit を入力用の形に落とす。
 * 種別と転換日数は常にセットなので、転換しない (種別 null) unit は転換日数も null にそろえる
 */
export function toManuallyClaimingUnitInputs(
	units: {
		minVisitCount: number
		manuallyClaimingUnitKind?: ManuallyClaimingUnitKind | null
		conversionDays?: number | null
	}[]
): ProcessedManuallyClaimingUnit[] {
	return units.map((unit) => {
		const kind = unit.manuallyClaimingUnitKind ?? null
		let conversionDays: number | null
		if (kind === null) {
			conversionDays = null
		} else {
			conversionDays = unit.conversionDays ?? null
		}
		return {
			minVisitCount: unit.minVisitCount,
			manuallyClaimingUnitKind: kind,
			conversionDays,
		}
	})
}

/**
 * 請求機能の利用有無に応じてキャンセル料フィールドを処理
 */
export function processCancelTypeSetting(
	row: CompanySmsSendingRuleV2EditCancelTypeSetting
): ProcessedCancelTypeSetting {
	// 請求機能の利用判定: minVisitCountForEncouragingRereservation >= 1 の場合に有効
	const isClaimingEnabled = isClaimingFunctionEnabled(
		row.minVisitCountForEncouragingRereservation
	)

	// 再予約案内を使用しない場合は unit を送らない (全削除)
	let encouragingRereservationUnits: ProcessedEncouragingRereservationUnit[]
	if (
		isEncouragingRereservationEnabled(
			row.minVisitCountForEncouragingRereservation,
			row.minVisitCountForNothing
		)
	) {
		encouragingRereservationUnits = toEncouragingRereservationUnitInputs(
			row.encouragingRereservationUnits ?? []
		)
	} else {
		encouragingRereservationUnits = []
	}

	// 手動請求を使用しない場合は unit を送らない (全削除)
	let manuallyClaimingUnits: ProcessedManuallyClaimingUnit[]
	if (
		isManuallyClaimingEnabled(
			row.minVisitCountForManuallyClaimingCancelFee,
			row.minVisitCountForEncouragingRereservation
		)
	) {
		manuallyClaimingUnits = toManuallyClaimingUnitInputs(
			row.manuallyClaimingUnits ?? []
		)
	} else {
		manuallyClaimingUnits = []
	}

	if (!isClaimingEnabled) {
		return {
			id: row.id,
			cancelType: row.cancelType,
			claimingConversionPolicyName: toNullOfString(
				row.claimingConversionPolicyName
			),
			encouragingRereservationPolicyName: toNullOfString(
				row.encouragingRereservationPolicyName
			),
			encouragingRereservationUnits,
			manuallyClaimingUnits,
			minVisitCountForClaimingConversion:
				row.minVisitCountForClaimingConversion,
			minVisitCountForEncouragingRereservation:
				row.minVisitCountForEncouragingRereservation,
			minVisitCountForManuallyClaimingCancelFee:
				row.minVisitCountForManuallyClaimingCancelFee,
			minVisitCountForNothing: row.minVisitCountForNothing,

			// 請求機能を利用しない場合
			cancelFeePercentForNonZeroYenReservation: null,
			cancelFeeAmountForNonZeroYenReservation: null,
			cancelFeeAmountForZeroYenReservation: 0,
		}
	}

	// 請求機能を利用する場合は、選択されたタイプに応じて不要なフィールドをnullに
	if (
		row.cancelFeeTypeForNonZeroYenReservation ===
		cancelFeeTypeForNonZeroYenReservationKey.PERCENT
	) {
		// PERCENTタイプの場合、パーセンテージ値が必須
		if (row.cancelFeePercentForNonZeroYenReservation == null) {
			throw new Error(
				`cancelFeePercentForNonZeroYenReservation must be set when using PERCENT type for cancelType: ${row.cancelType}`
			)
		}
		return {
			id: row.id,
			cancelType: row.cancelType,
			cancelFeePercentForNonZeroYenReservation:
				row.cancelFeePercentForNonZeroYenReservation,
			cancelFeeAmountForNonZeroYenReservation: null,
			cancelFeeAmountForZeroYenReservation:
				row.cancelFeeAmountForZeroYenReservation,
			claimingConversionPolicyName: toNullOfString(
				row.claimingConversionPolicyName
			),
			encouragingRereservationPolicyName: toNullOfString(
				row.encouragingRereservationPolicyName
			),
			encouragingRereservationUnits,
			manuallyClaimingUnits,
			minVisitCountForClaimingConversion:
				row.minVisitCountForClaimingConversion,
			minVisitCountForEncouragingRereservation:
				row.minVisitCountForEncouragingRereservation,
			minVisitCountForManuallyClaimingCancelFee:
				row.minVisitCountForManuallyClaimingCancelFee,
			minVisitCountForNothing: row.minVisitCountForNothing,
		}
	} else if (
		row.cancelFeeTypeForNonZeroYenReservation ===
		cancelFeeTypeForNonZeroYenReservationKey.AMOUNT
	) {
		// AMOUNTタイプの場合、金額値が必須
		if (row.cancelFeeAmountForNonZeroYenReservation == null) {
			throw new Error(
				`cancelFeeAmountForNonZeroYenReservation must be set when using AMOUNT type for cancelType: ${row.cancelType}`
			)
		}
		return {
			id: row.id,
			cancelType: row.cancelType,
			cancelFeePercentForNonZeroYenReservation: null,
			cancelFeeAmountForNonZeroYenReservation:
				row.cancelFeeAmountForNonZeroYenReservation,
			cancelFeeAmountForZeroYenReservation:
				row.cancelFeeAmountForZeroYenReservation,
			claimingConversionPolicyName: toNullOfString(
				row.claimingConversionPolicyName
			),
			encouragingRereservationPolicyName: toNullOfString(
				row.encouragingRereservationPolicyName
			),
			encouragingRereservationUnits,
			manuallyClaimingUnits,
			minVisitCountForClaimingConversion:
				row.minVisitCountForClaimingConversion,
			minVisitCountForEncouragingRereservation:
				row.minVisitCountForEncouragingRereservation,
			minVisitCountForManuallyClaimingCancelFee:
				row.minVisitCountForManuallyClaimingCancelFee,
			minVisitCountForNothing: row.minVisitCountForNothing,
		}
	} else {
		// cancelFeeTypeForNonZeroYenReservationが不正な値の場合のエラー
		throw new Error(
			`Invalid cancelFeeTypeForNonZeroYenReservation for cancelType: ${row.cancelType}. Expected PERCENT or AMOUNT but got: ${row.cancelFeeTypeForNonZeroYenReservation}`
		)
	}
}

/**
 * フォーム入力を処理して送信用のデータに変換
 */
export function processFormInput(
	input: CompanySmsSendingRuleV2EditType
): ProcessedInput {
	return {
		id: input.id,
		name: input.name,
		cancelPolicyTemplateType: input.cancelPolicyTemplateType,
		cancelTypeSettingInputs: input.cancelTypeSettingInputs.map(
			processCancelTypeSetting
		),
	}
}

/**
 * UPDATE mutation用のinputを準備
 */
export function prepareUpdateInput(
	processedInput: ProcessedInput,
	companyId: string
) {
	return {
		companyId,
		id: processedInput.id,
		name: processedInput.name,
		cancelTypeSettingInputs: processedInput.cancelTypeSettingInputs.map(
			(row) => {
				// UPDATE時はidが必須
				if (!row.id) {
					throw new Error(
						`ID is required for UPDATE mutation. CancelType: ${row.cancelType}`
					)
				}
				// cancelTypeとcancelFeeTypeForNonZeroYenReservationを除外
				return {
					id: row.id,
					cancelFeePercentForNonZeroYenReservation:
						row.cancelFeePercentForNonZeroYenReservation,
					cancelFeeAmountForNonZeroYenReservation:
						row.cancelFeeAmountForNonZeroYenReservation,
					cancelFeeAmountForZeroYenReservation:
						row.cancelFeeAmountForZeroYenReservation,
					claimingConversionPolicyName: row.claimingConversionPolicyName,
					encouragingRereservationPolicyName:
						row.encouragingRereservationPolicyName,
					encouragingRereservationUnits: row.encouragingRereservationUnits,
					manuallyClaimingUnits: row.manuallyClaimingUnits,
					minVisitCountForClaimingConversion:
						row.minVisitCountForClaimingConversion,
					minVisitCountForEncouragingRereservation:
						row.minVisitCountForEncouragingRereservation,
					minVisitCountForManuallyClaimingCancelFee:
						row.minVisitCountForManuallyClaimingCancelFee,
					minVisitCountForNothing: row.minVisitCountForNothing,
				}
			}
		),
	}
}

/**
 * CREATE mutation用のinputを準備
 */
export function prepareCreateInput(
	processedInput: ProcessedInput,
	companyId: string
) {
	// CREATE時はcancelPolicyTemplateTypeが必須
	if (!processedInput.cancelPolicyTemplateType) {
		throw new Error(`CancelPolicyTemplateType is required for CREATE mutation`)
	}
	return {
		companyId,
		name: processedInput.name,
		cancelPolicyTemplateType: processedInput.cancelPolicyTemplateType,
		cancelTypeSettingInputs: processedInput.cancelTypeSettingInputs.map(
			(row) => {
				// CREATE時はcancelTypeが必須
				if (!row.cancelType) {
					throw new Error(`CancelType is required for CREATE mutation`)
				}
				// idとcancelFeeTypeForNonZeroYenReservationを除外
				return {
					cancelType: row.cancelType,
					cancelFeePercentForNonZeroYenReservation:
						row.cancelFeePercentForNonZeroYenReservation,
					cancelFeeAmountForNonZeroYenReservation:
						row.cancelFeeAmountForNonZeroYenReservation,
					cancelFeeAmountForZeroYenReservation:
						row.cancelFeeAmountForZeroYenReservation,
					claimingConversionPolicyName: row.claimingConversionPolicyName,
					encouragingRereservationPolicyName:
						row.encouragingRereservationPolicyName,
					encouragingRereservationUnits: row.encouragingRereservationUnits,
					manuallyClaimingUnits: row.manuallyClaimingUnits,
					minVisitCountForClaimingConversion:
						row.minVisitCountForClaimingConversion,
					minVisitCountForEncouragingRereservation:
						row.minVisitCountForEncouragingRereservation,
					minVisitCountForManuallyClaimingCancelFee:
						row.minVisitCountForManuallyClaimingCancelFee,
					minVisitCountForNothing: row.minVisitCountForNothing,
				}
			}
		),
	}
}

// select は状態によって null だったり空文字だったりするので、それを null に変換
const toNullOfString = (value: any): string | null => {
	if (value === null || value === undefined || value === "") {
		return null
	}
	return String(value)
}
